import axios from "axios";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";  // Correct import
import { logoutUser } from '../store/actions/authActions'; // Import your logout action
import store from '../store';

const RouteApi = () => {
    const { id } = useParams(); // Retrieve the dynamic id from the route params
    const [primary, setPrimaryPopulation] = useState(0);
    const [secondary, setSecondaryPopulation] = useState(0);
    const [students, setStudents] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [aiRecommendations, setAiRecommendations] = useState([]); // ✅ AI recommendations
    const [aiResponse, setAiResponse] = useState(""); // ✅ AI-generated response
    const [decodedSchoolToken, setDecodedSchoolToken] = useState(null);
    const [decodedStudentToken, setDecodedStudentToken] = useState(null);
    const [decodedTeacherToken, setDecodedTeacherToken] = useState(null);
    const [decodedTaskToken, setDecodedTaskToken] = useState(null);

    const [pendingConfirmation, setPendingConfirmation] = useState(null);

    // Local development goes through CRA's proxy, while a deployed build can
    // provide REACT_APP_API_ORIGIN when the API is hosted on another domain.
    const apiOrigin = (process.env.REACT_APP_API_ORIGIN || "").replace(/\/$/, "");
    const axiosInstance = axios.create({
        // Empty in production (same origin); set REACT_APP_API_ORIGIN locally to use Hostinger.
        baseURL: `${apiOrigin}/${id}/v3`,
        headers: {
            "Content-Type": "application/json",
        },
    });

    axiosInstance.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response && error.response.status === 401) {
                // Dispatch logout when token expires or is invalid
                store.dispatch(logoutUser());
                window.location.href = `/#/${id}`;
            }
            return Promise.reject(error);
        }
    );

    // Students Submit function
    const handleStudents = async (studentsData, id) => {
        try {
            const token = localStorage.getItem("jwtToken"); // Get JWT token

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post("/upload_students",
                { students: studentsData, school_id: id },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Students uploaded successfully:", response.data);
            //alert("Students uploaded successfully!");
            return response.data;
        } catch (error) {
            console.error("Error uploading students:", error.response?.data || error.message);
            //alert("Failed to upload students.");
        }
    };

    // Teachers Submit function
    const handleTeachers = async (teachersData, id) => {
        try {
            const token = localStorage.getItem("jwtToken"); // Get JWT token

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post("/upload_teachers",
                { teachers: teachersData, school_id: id },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Teachers uploaded successfully:", response.data);
            // alert("Teachers uploaded successfully!");
            return response.data;
        } catch (error) {
            console.error("Error uploading teachers:", error.response?.data || error.message);
            // alert("Failed to upload teachers.");
        }
    };

    const handleSubjects = async (subjects, id) => {
        try {
            const token = localStorage.getItem("jwtToken"); // Get JWT token

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post("/upload_subjects",
                { subjects, school_id: id },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Subjects uploaded successfully:", response.data);
            return response.data; // Return the response data directly
        } catch (error) {
            console.error("Error uploading subjects:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to upload subjects.",
            };
        }
    };

    const handleuploadFiles = async (file, id) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const formData = new FormData();
            formData.append("file", file);
            formData.append("school_id", id);

            const response = await axiosInstance.post("/upload_student_file.php", formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            console.log("Students uploaded successfully:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error uploading students:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to upload student file.",
            };
        }
    };

    // ✅ Fetch AI Recommendations
    const getAiRecommendations = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.get("/smart-ai/recommendations", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data?.recommendations) {
                setAiRecommendations(response.data.recommendations);
                console.log("AI Recommendations:", response.data.recommendations);
            }

        } catch (error) {
            console.error("Error fetching AI recommendations:", error);
        }
    };

    // ✅ AI-Assisted Task Creation
    const generateAiTask = async (tokenId, confirmationResponse = null) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            // Step 1: Request AI Task Generation
            const response = await axiosInstance.post(
                "/smart-ai/generate-task",
                { token_id: tokenId, confirmation: confirmationResponse ?? undefined },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const data = response.data;

            // Step 2: Check for missing fields in dispatch task
            if (data?.missing_fields?.length > 0) {
                const missingInfo = data.missing_fields.join(", ");
                setAiResponse(
                    `⚠️ Missing Information Detected:\n\n${missingInfo}\n\n📝 Hint: ${data.hint}`
                );
                return;
            }

            // Step 3: If confirmation is already sent and result was executed
            if (confirmationResponse === "yes" && data.execution_result) {
                const resultObj = typeof data.execution_result === "object"
                    ? data.execution_result
                    : JSON.parse(data.execution_result);

                // Build a readable summary string
                const emailStatus = resultObj.email_status ? "Email sent successfully." : "Email not sent.";
                const smsStatusList = resultObj.sms_status.map(sms => {
                    return `• Student: ${sms.student}\n  Phone: ${sms.phone}\n  SMS Status: ${sms.sms_response.message} (Code: ${sms.sms_response.code})\n  Message ID: ${sms.sms_response.message_id}`;
                }).join("\n\n");

                const readableResponse =
                    `✅ Smart AI executed the task successfully:

                ${emailStatus}

                SMS Status:
                ${smsStatusList}

                🎉 Thank you for trusting me.`;

                setAiResponse(readableResponse);
                getNotifications(); // Fetch notifications after task execution
                return data;
            }

            // Step 4: Initial AI response with detected values
            const {
                task_name,
                task_description,
                task_type,
                task_priority,
                ai_suggestion,
                confirmation,
                detected_result_type,
                detected_class
            } = data;

            let detectedDetails = "";
            if (detected_result_type || detected_class) {
                detectedDetails += "\n\n🧠 Smart Detection:\n";
                if (detected_result_type) detectedDetails += `📘 Result Type: ${detected_result_type}\n`;
                if (detected_class) detectedDetails += `🏫 Class: ${detected_class}`;
            }

            // Ask for confirmation if not already provided
            if (!confirmationResponse) {
                setAiResponse(
                    `🧠 Smart AI Suggestion:\n\n ${ai_suggestion}${detectedDetails}\n\n❓ Would you like Smart AI to proceed with this task?`
                );

                // Show Yes/No buttons
                setPendingConfirmation({ token_id: tokenId });
                return;
            }


        } catch (error) {
            console.error("Error generating AI task:", error);

            if (error.response?.data?.error) {
                setAiResponse(`❌ Error: ${error.response.data.error}`);
            } else {
                setAiResponse(`❌ An unexpected error occurred while processing the task.`);
            }

            throw error;
        }
    };

    // ✅ Fetch AI Response from an AI API
    const getAiResponse = async (chatHistory) => {
        try {
            const token = localStorage.getItem("jwtToken");

            // ✅ Get last user message
            const latestUserMessage = [...chatHistory]
                .reverse()
                .find(msg => msg.role === 'user')?.content;

            if (!latestUserMessage) {
                console.warn("No user message found.");
                return null;
            }

            // ✅ Send to backend
            const response = await axiosInstance.post(
                "/smart-ai/chat",
                { query: latestUserMessage },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = response.data;

            // ✅ Detect AI action
            if (data.action && data.action === "generate_exercise") {
                const exe_id = data.result?.token_id || data.metadata?.token_id;

                if (exe_id) {
                    console.log("🎯 Redirecting to exercise page:", exe_id);
                    window.location.href = `/#/${id}/exercise/${exe_id}`;
                } else {
                    console.warn("⚠️ No token_id found in result or metadata.");
                }
                return null; // stop further message handling
            }

            // ✅ Otherwise, return normal AI text
            if (data.response) {
                return data.response;
            } else {
                console.warn("AI did not return a response.");
                return null;
            }

        } catch (error) {
            console.error("Error fetching AI response:", error);
            return null;
        }
    };
    

    // ✅ Fetch Primary and Secondary Population from School Info
    const getSchoolPopulation = async () => {
        try {
            const token = localStorage.getItem("jwtToken"); // Use consistent token name

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.get("/school-info", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { token: responseToken } = response.data;

            if (responseToken) {
                const decoded = jwtDecode(responseToken);  // Correctly decode the token
                setDecodedSchoolToken(decoded);
                console.log("Decoded school token:", decoded);

                if (decoded?.primary_population && decoded?.secondary_population) {
                    setPrimaryPopulation(decoded.primary_population);
                    setSecondaryPopulation(decoded.secondary_population);
                }
            }

        } catch (error) {
            console.error("Error fetching school population:", error);
        }
    };

    const getCollectionFromResponse = (data, collectionName) => {
        // Some shared-host PHP configurations serve JSON as text/html. Axios
        // normally parses it, but accept the raw string as well so a valid API
        // response can never leave the dashboard tables empty.
        if (typeof data === "string") {
            try {
                data = JSON.parse(data);
            } catch {
                return [];
            }
        }

        if (Array.isArray(data)) return data;
        if (Array.isArray(data?.[collectionName])) return data[collectionName];
        if (Array.isArray(data?.data?.[collectionName])) return data.data[collectionName];

        if (data?.token) {
            const decoded = jwtDecode(data.token);
            return Array.isArray(decoded?.[collectionName]) ? decoded[collectionName] : [];
        }

        return [];
    };

    // ✅ Fetch Entire Student Data
    const getStudents = async (attempt = 0) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { success: false, message: "You are not authenticated." };
            }

            const response = await axiosInstance.get("/students", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Keep the client state aligned with the server rule. This also
            // prevents stale cached API responses from displaying archived rows.
            const studentRows = getCollectionFromResponse(response.data, "students")
                .filter((student) => String(student?.stud_id || "").trim() !== "");
            // Hostinger occasionally returns a plain-text database error while
            // recycling a connection. Retry only that non-JSON response so a
            // transient failure never clears an otherwise healthy directory.
            if (typeof response.data === "string" && attempt < 2) {
                await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
                return getStudents(attempt + 1);
            }
            if (response.data?.token) setDecodedStudentToken(jwtDecode(response.data.token));
            setStudents(studentRows);
            return { success: true, people: studentRows };

        } catch (error) {
            console.error("Error fetching students:", error);
            return { success: false, message: error.response?.data?.message || `Unable to load students${error.response?.status ? ` (server error ${error.response.status})` : "."}` };
        }
    };

    // ✅ Fetch Entire Teacher Data
    const getTeachers = async (attempt = 0) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { success: false, message: "You are not authenticated." };
            }

            const response = await axiosInstance.get("/teachers", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const teacherRows = getCollectionFromResponse(response.data, "teachers");
            if (typeof response.data === "string" && attempt < 2) {
                await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
                return getTeachers(attempt + 1);
            }
            if (response.data?.token) setDecodedTeacherToken(jwtDecode(response.data.token));
            setTeachers(teacherRows);
            return { success: true, people: teacherRows };

        } catch (error) {
            console.error("Error fetching teachers:", error);
            return { success: false, message: error.response?.data?.message || `Unable to load teachers${error.response?.status ? ` (server error ${error.response.status})` : "."}` };
        }
    };

    const getSubjects = async (schoolId, setSubjects) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.get(`/subjects?school_id=${schoolId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { subjects, success } = response.data;

            if (success && Array.isArray(subjects)) {
                setSubjects(subjects);
            } else {
                console.error("Failed to fetch subjects:", response.data.message);
            }
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };

    // ✅ Fetch Task List
    const getTasks = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.get("/get-tasks", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { token: responseToken } = response.data;

            if (responseToken) {
                const decoded = jwtDecode(responseToken);
                setDecodedTaskToken(decoded);
                console.log("Decoded task token:", decoded);

                if (decoded?.tasks) {
                    setTasks(decoded.tasks);
                }
            }

        } catch (error) {
            console.error("Error fetching tasks:", error);
        }
    };

    const editTask = async (task_id, updatedData) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            // ✅ Send updated task data to backend using POST
            const response = await axiosInstance.post(
                `/update-task`, // Use POST for update task
                { task_id, ...updatedData }, // Pass task_id along with updatedData in the body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Task Updated Successfully:", response.data);

            getTasks(); // ✅ Refresh the task list after update
            return response.data;

        } catch (error) {
            console.error("Error updating task:", error.response?.data || error.message);
            throw error;
        }
    };

    // ✅ Function to Submit Create Task Form
    const submitCreateTask = async (taskData) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post("/create-task", taskData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("Task Created Successfully:", response.data);
            getTasks(); // ✅ Refresh task list after adding a new task
            return response.data;

        } catch (error) {
            console.error("Error creating task:", error.response?.data || error.message);
            throw error;
        }
    };

    const getNotifications = async () => {
        const token = localStorage.getItem("jwtToken");

        if (!token) {
            console.error("No token found. User is not authenticated.");
            return [];
        }

        try {
            const response = await axiosInstance.get("/notifications", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.status === 200 && response.data?.data) {
                return response.data.data; // assuming { message: "...", data: [...] }
            } else {
                console.warn("Unexpected response:", response.data);
                return [];
            }
        } catch (error) {
            if (error.response) {
                console.error("API error:", error.response.data);
            } else {
                console.error("Network error:", error.message);
            }
            return [];
        }
    };

    const handleCbtSubmit = async (cbtData) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post("/cbt/create", cbtData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("CBT Created Successfully:", response.data);
            getCbtList(); // ✅ Optionally refresh CBT list if you have this method
            return response.data;

        } catch (error) {
            console.error("Error creating CBT:", error.response?.data || error.message);
            throw error;
        }
    };

    const getCbtList = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found");
                return { success: false, cbts: [] };
            }

            const response = await axiosInstance.get("/cbt/my-tasks", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { success, tasks } = response.data;

            return { success, tasks: Array.isArray(tasks) ? tasks : [] }; // ✅ Always return a clean array
        } catch (err) {
            console.error("Error fetching CBTs:", err);
            return { success: false, tasks: [] };
        }
    };

    const getCbtByTokenId = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No JWT token found in localStorage");
                return { success: false, task: null };
            }

            const response = await axiosInstance.get(`/cbt/task-by-token?token_id=${tokenId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { success, task } = response.data;

            return { success, task };
        } catch (err) {
            console.error("Error fetching CBT by token ID:", err);
            return { success: false, task: null };
        }
    };

    const handleCbtColumn = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/cbt/create/column",
                { tokenid: tokenId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("CBT column created:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error creating CBT column:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to create CBT column.",
            };
        }
    };

    const handleUpdateCbtColumn = async (updateData) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/cbt/update/column",
                updateData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("CBT column updated:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error updating CBT column:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to update CBT column.",
            };
        }
    };

    const handleGetAllCbtColumns = async (tokenId, email = null) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/cbt/columns/all",
                { tokenid: tokenId, email },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("All CBT columns fetched:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching all CBT columns:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to fetch CBT columns.",
            };
        }
    };

    const handleGetCbtColumnById = async (id, tokenId, email = null) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/cbt/column/get",
                { id, tokenid: tokenId, email },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("CBT column fetched by ID:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching CBT column by ID:", error.response?.data || error.message);
            return {
                status: false,
                message: error.response?.data?.message || "Failed to fetch CBT column.",
            };
        }
    };

    const saveStepperProgress = async (columnId, tokenId) => {
        if (!tokenId || !columnId) {
            console.log("❌ Missing Token ID or Column ID");
            return;
        }

        try {
            const token = localStorage.getItem("jwtToken");

            const response = await axiosInstance.post(
                "/cbt/update/stepper",
                {
                    tokenid: tokenId,
                    stepper: columnId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                localStorage.setItem("lastSelectedCbtColumnId", columnId);
                //toast.success("✅ Stepper progress saved!");
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error("Error saving stepper:", error);
            //toast.error(`❌ Failed to save stepper progress`);
        }
    };

    const deleteCbtByToken = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) throw new Error("Unauthorized");

            const response = await axiosInstance.post(
                "/cbt/delete",
                { tokenid: tokenId },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Delete CBT Error:", error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || "Failed to delete CBT.",
            };
        }
    };

    // Exercise Starts Here //
    const handleExerciseSubmit = async (ExerData) => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post("/exercise/create", ExerData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            console.log("CBT Created Successfully:", response.data);
            getCbtList(); // ✅ Optionally refresh CBT list if you have this method
            return response.data;

        } catch (error) {
            console.error("Error creating CBT:", error.response?.data || error.message);
            throw error;
        }
    };

    const getExerciseList = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            if (!token) {
                console.error("No token found");
                return { success: false, cbts: [] };
            }

            const response = await axiosInstance.get("/exercise/my-tasks", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const { success, tasks } = response.data;

            return { success, tasks: Array.isArray(tasks) ? tasks : [] }; // ✅ Always return a clean array
        } catch (err) {
            console.error("Error fetching CBTs:", err);
            return { success: false, tasks: [] };
        }
    };

    // ✅ Exercise API Helpers
    // Ensure axiosInstance is already configured with baseURL and interceptors.

    const getExerciseByTokenId = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) {
                console.error("No JWT token found in localStorage");
                return { success: false, task: null };
            }

            const response = await axiosInstance.get(`/exercise/task-by-token?token_id=${tokenId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const { success, task } = response.data;
            return { success, task };
        } catch (err) {
            console.error("Error fetching Exercise by token ID:", err);
            return { success: false, task: null };
        }
    };

    const handleExerciseColumn = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/exercise/create/column",
                { tokenid: tokenId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("Exercise column created:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error creating Exercise column:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to create Exercise column.",
            };
        }
    };

    const handleUpdateExerciseColumn = async (updateData) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/exercise/update/column",
                updateData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("Exercise column updated:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error updating Exercise column:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to update Exercise column.",
            };
        }
    };

    const handleGetAllExerciseColumns = async (tokenId, email = null) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/exercise/columns/all",
                { tokenid: tokenId, email },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("All Exercise columns fetched:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching all Exercise columns:", error.response?.data || error.message);
            return {
                status: "error",
                message: error.response?.data?.message || "Failed to fetch Exercise columns.",
            };
        }
    };

    const handleGetExerciseColumnById = async (id, tokenId, email = null) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) {
                console.error("No token found. User is not authenticated.");
                return { status: "error", message: "Unauthorized access" };
            }

            const response = await axiosInstance.post(
                "/exercise/column/get",
                { id, tokenid: tokenId, email },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            console.log("Exercise column fetched by ID:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error fetching Exercise column by ID:", error.response?.data || error.message);
            return {
                status: false,
                message: error.response?.data?.message || "Failed to fetch Exercise column.",
            };
        }
    };

    const saveExerciseStepperProgress = async (columnId, tokenId) => {
        if (!tokenId || !columnId) {
            console.log("❌ Missing Token ID or Column ID");
            return;
        }

        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/exercise/update/stepper",
                { tokenid: tokenId, stepper: columnId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (response.data.success) {
                localStorage.setItem("lastSelectedExerciseColumnId", columnId);
                // toast.success("✅ Stepper progress saved!");
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            console.error("Error saving exercise stepper:", error);
            // toast.error(`❌ Failed to save stepper progress`);
        }
    };

    const deleteExerciseByToken = async (tokenId) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) throw new Error("Unauthorized");

            const response = await axiosInstance.post(
                "/exercise/delete",
                { tokenid: tokenId },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Delete Exercise Error:", error.response?.data || error.message);
            return {
                success: false,
                message: error.response?.data?.message || "Failed to delete Exercise.",
            };
        }
    };

    // ENDS HERE //
    const getResultList = async (resultType) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.get(`/results?type=${resultType}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        } catch (error) {
            console.error("Error fetching results:", error.response?.data || error.message);
            return { status: "error", message: "Failed to fetch results." };
        }
    };

    const sendResultToParent = async (stud_id, resultType) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                '/send_result',
                { stud_id, resultType },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error sending result:", error.response?.data || error.message);
            return { status: "error", message: error.response?.data.message || "Failed to send result." };
        }
    };

    const fetchReportCard = async (stud_id, resultType) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/get-report-card",
                { stud_id, resultType },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching report card:", error.response?.data || error.message);
            return {
                status: "error",
                message: "Unable to fetch report card",
            };
        }
    };

    const fetchWalletData = async (email) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/wallet",
                { email }, // Send in request body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error fetching wallet data:", error.response?.data || error.message);
            return { status: "error", message: "Failed to fetch wallet data." };
        }
    };

    // ✅ Store Paystack transaction after successful payment
    const storePayment = async ({ email, amount, reference }) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/pay/initialize", // your backend endpoint
                { email, amount, reference },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error("Error storing payment:", error.response?.data || error.message);
            return { status: "error", message: "Failed to store payment" };
        }
    };

    const verifyPayment = async (reference) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/pay/verify", // your backend route
                { reference }, // Send the Paystack reference in the body
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error(
                "Error verifying payment:",
                error.response?.data || error.message
            );
            return { status: "error", message: "Payment verification failed." };
        }
    };

    const fetchAllPayments = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            const response = await axiosInstance.post(
                "/account/payments", // your backend route (AccountController)
                {}, // no body needed for now
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === "success") {
                return response.data.payments;
            } else {
                console.error("Fetch failed:", response.data.message);
                return [];
            }
        } catch (error) {
            console.error(
                "Error fetching payments:",
                error.response?.data || error.message
            );
            return [];
        }
    };

    const savePayType = async (data) => {
        try {
            const token = localStorage.getItem("jwtToken");

            const response = await axiosInstance.post(
                "/account/payment-type/create", // 👈 your backend endpoint (adjust as needed)
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === "success") {
                console.log("✅ Payment type saved:", response.data);
                return response.data;
            } else {
                console.error("❌ Save failed:", response.data.message);
                return null;
            }
        } catch (error) {
            console.error("❌ Error saving payment type:", error.response?.data || error.message);
            return null;
        }
    };      

    const fetchPaymentType = async () => {
        try {
            const token = localStorage.getItem("jwtToken");

            const response = await axiosInstance.post(
                "/account/payment-type/all", // 👈 your backend endpoint (adjust as needed)
                {}, // no data needed
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === "success") {
                console.log("✅ Payment types fetched:", response.data.data);
                return response.data.data;
            } else {
                console.error("❌ Fetch failed:", response.data.message);
                return [];
            }
        } catch (error) {
            console.error("❌ Error fetching payment types:", error.response?.data || error.message);
            return [];
        }
    };
    
    const fetchPaymentByToken = async (payid) => {
        try {

            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/account/get-payment-by-token",
                { token: payid },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.data.status === "success") {
                console.log("✅ Payment fetched:", response.data.payment);
                return response.data.payment;
            } else {
                console.error("❌ Fetch failed:", response.data.message);
                return null;
            }
        } catch (error) {
            console.error("❌ Error fetching payment by token:", error.response?.data || error.message);
            return null;
        }
    };

    const verifyStudPayment = async (reference) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axiosInstance.post(
                "/pay/student/verify",
                { reference },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data;
        } catch (error) {
            console.error(
                "Error verifying payment:",
                error.response?.data || error.message
            );
            return { status: "error", message: "Payment verification failed." };
        }
    };
    
    // 🔹 Send result to parent
    const sendAllResult = async (resultType) => {
        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axios.post(
                "/send_all_result",
                { resultType },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error("Error sending result:", error.response?.data || error.message);
            return {
                status: "error",
                message:
                    error.response?.data.message || "Failed to send result.",
            };
        }
    };

    // 🔹 Fetch result dispatch logs
    const fetchLogs = async (resultType) => {
        try {
            const token = localStorage.getItem("jwtToken");

            const response = await axios.get(
                `/api/result/logs?type=${resultType}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            return response.data; // contains { logs: [...] }
        } catch (error) {
            console.error("Error fetching logs:", error.response?.data || error.message);
            return { status: "error", message: "Failed to fetch logs." };
        }
    };

    // 🔹 Real-time streaming (SSE) version
    const sendAllResultStream = (resultType, onMessage, onDone, onError) => {
        try {
            const token = localStorage.getItem("jwtToken");

            // Build SSE URL with token (since headers aren't supported)
            const url = `${id}/v3/send_all_result?token=${encodeURIComponent(token)}&resultType=${encodeURIComponent(resultType)}`;

            // Open the SSE stream
            const eventSource = new EventSource(url);

            // Handle incoming messages
            eventSource.onmessage = (event) => {
                if (event.data === "done") {
                    onDone && onDone();
                    eventSource.close();
                } else {
                    onMessage && onMessage(event.data);
                }
            };

            // Handle errors
            eventSource.onerror = (err) => {
                console.error("SSE Error:", err);
                eventSource.close();
                onError && onError(err);
            };

            return eventSource; // allow manual close if needed
        } catch (error) {
            console.error("Error initializing stream:", error);
            onError && onError(error);
            return null;
        }
    };    

    const handleEditStudent = async (formData, id) => {
        try {
            const token = localStorage.getItem("jwtToken"); // Get JWT token
            if (!token) {
                console.error("No token found. User is not authenticated.");
                return;
            }

            const response = await axiosInstance.post(
                "/edit_student", // endpoint for editing student
                { id, ...formData },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Student updated successfully:", response.data);
            return response.data;

        } catch (error) {
            console.error(
                "Error updating student:",
                error.response?.data || error.message
            );
            return null;
        }
    };

    const handleEditTeacher = async (formData, id) => {
        try {
            const token = localStorage.getItem("jwtToken");
            if (!token) return { success: false, message: "You are not authenticated." };

            const response = await axiosInstance.post(
                "/edit_teacher",
                { id, ...formData },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await getTeachers();
            return response.data;
        } catch (error) {
            console.error("Error updating teacher:", error.response?.data || error.message);
            return error.response?.data || { success: false, message: "Unable to update teacher." };
        }
    };

    useEffect(() => {
        getSchoolPopulation();
        getTasks(); // ✅ Fetch tasks on component mount
        getCbtList(); // ✅ Fetch CBT list on component mount
    }, [id]); // Dependency on `id`, so it updates when the `id` changes

    useEffect(() => {
        // getAiRecommendations(); // ✅ Fetch AI recommendations on component mount
    }, [id]); // Dependency on `id`, so it updates when the `id` changes

    return {
        primary,
        secondary,
        students,
        teachers,
        tasks,
        editTask,
        aiRecommendations,  // ✅ AI recommendations for UI
        aiResponse,  // ✅ AI response for chat interactions
        decodedSchoolToken,
        decodedStudentToken,
        decodedTeacherToken,
        decodedTaskToken,
        submitCreateTask,
        generateAiTask,  // ✅ AI-assisted task creation function
        getAiResponse,  // ✅ Fetch AI responses
        getTasks,
        handleStudents,
        handleTeachers,
        handleSubjects,
        handleuploadFiles,
        pendingConfirmation,
        setPendingConfirmation,  // ✅ Function to set pending confirmation
        setAiResponse,  // ✅ Function to set AI response
        getNotifications,  // ✅ Fetch notifications
        getSubjects,  // ✅ Fetch subjects
        handleCbtSubmit,
        getCbtList,
        getCbtByTokenId,
        handleCbtColumn,
        handleUpdateCbtColumn,
        handleGetAllCbtColumns,
        handleGetCbtColumnById,
        saveStepperProgress,
        deleteCbtByToken,
        getResultList,
        sendResultToParent,
        fetchReportCard,  // ✅ Fetch report card
        fetchWalletData,
        storePayment,
        verifyPayment,
        fetchAllPayments,
        savePayType,
        fetchPaymentType,
        fetchPaymentByToken,
        verifyStudPayment,
        handleExerciseSubmit,
        getExerciseList,
        getExerciseByTokenId, 
        handleExerciseColumn, 
        handleUpdateExerciseColumn, 
        handleGetAllExerciseColumns, 
        handleGetExerciseColumnById, 
        saveExerciseStepperProgress, 
        deleteExerciseByToken,
        sendAllResult,
        fetchLogs,
        sendAllResultStream,
        handleEditStudent,
        handleEditTeacher,
        getStudents,
        getTeachers,
        axiosInstance
    };

};

export default RouteApi;
